import { focusHaloFlush, HapticSurface, panelMotion, press } from "@pax/ui";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, type LucideIcon } from "lucide-react";
import type { RefObject } from "react";
import { DeveloperMenu } from "@/components/DeveloperMenu";
import { NotificationsButton } from "@/components/NotificationsButton";
import { PaxGlobe } from "@/components/PaxGlobe";
import type { DevelopmentTool, DevelopmentToolsVisibility } from "@/development/preferences";
import { BarAccountToggle } from "@/features/auth/components/AccountToggles";
import { CreditsPill } from "@/features/credits/components/CreditsPill";
import type { HoverIntent } from "./use-hover-intent";

const markButton = `pointer-events-auto relative grid size-8 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-foreground/[0.07] hover:text-foreground ${panelMotion} ${press} ${focusHaloFlush}`;

/**
 * The chrome both arrangements share: the mark and the name at one end, your
 * credits and your picture at the other, and nothing in between.
 *
 * It is the lowest thing in the frame, so the screen covers it by sitting on
 * it rather than by asking it to fade. That is the whole of the reveal when
 * the sidebar gives way to the top bar.
 *
 * In the sidebar it is not a bar at all, it is the column's head, and it is
 * clipped to the column and slides and widens with it — so the two read as
 * one movement rather than a bar rearranging itself over a column that has
 * already left.
 */
export function ShellHead({
  mark,
  peek,
  markRef,
  pictureInColumn,
  onBack,
  developmentTools,
  onDevelopmentToolVisibilityChange,
}: {
  /**
   * What the mark does here, which is not the same thing in both
   * arrangements: on a desktop it changes which one you are in, and on a
   * phone it opens and closes the ways. Each says so in its own words and
   * with its own icon rather than sharing one and looking pressed.
   */
  mark: { icon: LucideIcon; label: string; expanded?: boolean; onPress: () => void };
  peek?: HoverIntent;
  /** So the frame can ask whether the pointer is still on the mark. */
  markRef?: RefObject<HTMLButtonElement | null>;
  /** Your picture is at the foot of the column instead, so this end is empty. */
  pictureInColumn: boolean;
  /** On your face of the column, the way back to the page you opened it from, in the brand's place. */
  onBack?: () => void;
  developmentTools: DevelopmentToolsVisibility;
  onDevelopmentToolVisibilityChange: (tool: DevelopmentTool, visible: boolean) => void;
}) {
  const Mark = mark.icon;

  return (
    // Its contents are centred in what can be seen of it rather than in the
    // whole of it: the screen covers its last few pixels, and anything centred
    // in the full height sits low in the part that is left.
    <header className="pointer-events-none absolute inset-x-0 top-0 flex h-(--bar-height) items-center justify-between pb-[calc(var(--bar-height)-var(--sheet-top))]">
      <div className="shell-head">
        <div className="shell-panes">
          <div className="flex h-full w-(--column-width) min-w-0 shrink-0 items-center gap-1.5 px-3">
            <span className="shell-mark flex shrink-0">
              <button
                ref={markRef}
                type="button"
                onClick={mark.onPress}
                aria-label={mark.label}
                aria-expanded={mark.expanded}
                className={markButton}
                {...peek}
              >
                <Mark aria-hidden className="size-4" />
                <HapticSurface />
              </button>
            </span>

            {/* The mark travels on a transform, so it keeps its place in here
                and the brand has somewhere of its own to go. */}
            <div className="shell-brand pointer-events-auto flex min-w-0 items-center gap-1.5">
              {onBack ? (
                <button type="button" onClick={onBack} aria-label="Back" className={markButton}>
                  <ArrowLeft aria-hidden className="size-4" />
                  <HapticSurface />
                </button>
              ) : (
                <>
                  <Link to="/" className="relative flex min-w-0 items-center gap-1.5">
                    <PaxGlobe />
                    <span className="truncate text-sm font-semibold tracking-tight">Pax.co</span>
                    <HapticSurface />
                  </Link>
                  <DeveloperMenu
                    developmentTools={developmentTools}
                    onDevelopmentToolVisibilityChange={onDevelopmentToolVisibilityChange}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        inert={pictureInColumn}
        className="shell-bar-avatar pointer-events-auto mr-3 flex items-center gap-1"
      >
        <CreditsPill />
        <NotificationsButton />
        <BarAccountToggle />
      </div>
    </header>
  );
}
